import { IColor4 } from '@inter-ikea-betrakta/viewer';
import { IconButtonSizeEnum } from '@inter-ikea-kompis/component-icon-button';
import { SkapaTheme } from '@inter-ikea-kompis/themes/lib';
import { IShoppingProduct } from '@inter-ikea-kompis/types';
import { IUnitValues } from 'i18n/parse';
import { AppRoutesEnum } from 'routes';
import { getTheme } from 'utils/getTheme';

export const IKEA_GLOBAL_HOME = 'https://www.ikea.com/';

export const QUERY_PARAMS = {
  COMPUTER_ID: 'computerId',
  KIOSK_ID: 'kioskId',
  STORE_ID: 'storeId',
  FORCE_VARIATION: 'forceVariation',
  DESIGN_CODE: 'designCode',
  UI_PLATFORM: 'uiPlatform',
  IPEX_GALLERY: 'ipexGallery',
  IDLE_TIME: 'idleTime',
  VPC_SOURCE: 'vpcSource',
  LOCALE: 'locale',
  LIGHTHOUSE: 'lighthouse',
  VERBOSE_SENTRY: 'verboseSentry',
  UNSAFE: 'unsafe',
  FUTURE_TOKEN: 'futureToken',
  FUTURE_DATE: 'futureDate',
  STORAGE_DESIGN_CODE: 'storageDesignCode',
  TEST_LOCALE: 'testLocale',
  ENABLE_VIRTUAL_KEYBOARD: 'enableVirtualKeyboard',
  FORCE_RTL: 'forceRtl',
  FORCE_PRODUCTION: 'forceProduction',
  ORIGIN: 'origin',
  CODE: 'code',
  STATE: 'state'
};

export const PLATFORMS = {
  WEB: 'web',
  KIOSK: 'kiosk'
};

/**
 * Don't forget to add the VPC to the sprs in the dexf application service.
 * We need this map to handle SPR redirects when a user enters from a page with a SPR ID,
 * DEXF application service is not initiated at that time.
 */
enum SprCodesEnum {
  S09422736 = 'QL4FGJ',
  S09422741 = 'TGJNPG',
  S09477917 = 'QTCFL9',
  S09575847 = 'QTJ396',
  S09575908 = 'QTJ6N3',
  S09575913 = 'QTJ4GN',
  S19424042 = 'QL4GFV',
  S19499570 = 'QTHZKD',
  S19575861 = 'QTC22S',
  S69575905 = 'QTCDZQ',
  S19575903 = 'SG796P',
  S19575917 = 'QL4FVM',
  S29422735 = 'QTCBJ9',
  S29422740 = 'QTCDKJ',
  S29424051 = 'TGBNF8',
  S29499584 = 'RMMKPY',
  S29575851 = 'QTJ6C9',
  S29575926 = 'QTJ56M',
  S29575931 = 'QTJ73D',
  S39424041 = 'QTCB7K',
  S39499574 = 'QTJ22P',
  S39575855 = 'QTBZLP',
  S39575916 = 'QL4G4M',
  S49424050 = 'TGJNZW',
  S49575906 = 'QTJ6SC',
  S49575911 = 'QTJ45F',
  S59422734 = 'QTC9CZ',
  S59575859 = 'QTC2FQ',
  S69422738 = 'RMMKLB',
  S69422743 = 'QL4GKG',
  S69424049 = 'QTCDBB',
  S69477919 = 'QTCFZB',
  S69499582 = 'RMMKRS',
  S69575849 = 'QTJ2YN',
  S79575924 = 'QTJ5NK',
  S89424048 = 'QL4FBP',
  S89499576 = 'QTHZW5',
  S89575853 = 'QTJ65Q',
  S89575928 = 'QTJ7D8',
  S99499571 = 'QTHZ7D',
  S99575857 = 'QL4GZ4',
  S19575922 = 'TGBNPS',
  S39575921 = 'TGJP8R',
  S39575935 = 'SG77YK',
  S69575934 = 'SG78BD'
}

export const SprCodes: IStringMap = SprCodesEnum;

export type IStringMap = Record<string, string>;

export type INumberMap = Record<string, number>;

export type IElementMap = Record<string, React.JSX.Element | null>;

export type IUnitValuesMap = Record<string, string | IUnitValues>;

export type IUnknownMap = Record<string, unknown>;

export type IShoppingProductsMap = Record<string, IShoppingProduct>;

export type ISceneColors = Record<string, IColor4>;

export type IStorageBehindMaterials = Record<
  string,
  {
    parent: number;
    default: number;
  }
>;

export type IVisibleProducts = Record<string, { quantity: number; id: string }[]>;

export const LOCAL_STORAGE_PREFIX = 'SKYTTA__';
export const LOCAL_STORAGE_CORE_CONFIG = `${LOCAL_STORAGE_PREFIX}CORE_CONFIG`;
export const SCREENSHOT_ERROR_MESSAGE = 'Failed to capture screenshot';
export const SPR_REGEX = /(?:#S)?(\d{8})(?=[&?]|$)/; // Matches format #S29422740
export const VPC_REGEX = /#([A-Z0-9]{6,})/; // Matches format #7TQYYV

export enum InsightsEventsEnum {
  ArrivedWithSpr = 'arrived_with_spr',
  PanelPatternsOpened = 'open_pattern_section',
  StoragebehindOpened = 'storage_behind_sheet_opened'
}

export const CORE_TO_VIEWER_SCALE = 1000;
export const MM_PER_CM = 10;
export const PANELS_PER_DOOR_FRAME = 4;

export enum SheetEnum {
  HamburgerMenu = 'hamburgerMenu',
  OpenGuide = 'openGuide',
  OpenDesign = 'openDesign',
  ShareDesign = 'shareDesign',
  MyDesigns = 'myDesigns',
  FAQ = 'faq',
  CustomizeSkytta = 'customizeSkytta',
  CuttingDoorsCta = 'cuttingDoorsCta',
  DistancePiecesCta = 'distancePiecesCta',
  WhatsIncluded = 'whatsIncluded',
  ProductDetails = 'productDetails',
  Help = 'help',
  BookAMeeting = 'BookAMeeting',
  StartFromScratch = 'startFromScratch',
  HomeDelivery = 'homeDelivery',
  StoreSelector = 'storeSelector',
  LanguageSelector = 'languageSelector',
  StorageBehind = 'storageBehind',
  StorageBehindGallery = 'storageBehindGallery',
  StorageBehindImported = 'storageBehindImported',
  UnusedPanels = 'unusedPanels',
  // CustomizeMenu sheets
  CustomizeMeasurements = 'customizeMeasurements',
  CustomizeDoorLayouts = 'customizeDoorLayouts',
  CustomizeFrames = 'customizeFrames',
  CustomizePanels = 'customizePanels',
  CustomizePanelPatterns = 'customizePanelsPatterns',
  CustomizeChr = 'customizeChr',
  CustomizeStopbars = 'customizeStopbars',
  CustomizeDoorHeights = 'customizeDoorHeights'
}

export enum RangeFamilyEnum {
  Panels = 'sliding_door_panels',
  Skytta = 'skytta'
}

export enum AreaEnum {
  MainNavigation = 'mainNavigation',
  Faq = 'faq'
}

export enum SheetSectionsEnum {
  Content = 'content',
  Footer = 'footer'
}

export enum NavigationSectionsEnum {
  Measurements = 'measurements',
  DoorLayouts = 'doorLayouts',
  DoorHeights = 'doorHeights',
  Frames = 'frames',
  Panels = 'panels',
  Patterns = 'patterns',
  Chr = 'chr',
  StopBars = 'stopBars',
  StorageBehind = 'storageBehind',
  StorageBehindGallery = 'storageBehindGallery',
  StorageBehindImported = 'storageBehindImported'
}

export enum AppStateEnum {
  Initial = 'initial',
  /**
   * When app has loaded product data and localized information
   */
  Ready = 'ready',
  ErrorPage = 'errorPage'
}

export const defaultViewerCalculations = {
  /**
   * Values in X,Y,Z
   */
  positionXYZ: {
    x: 0,
    y: 1.6,
    z: 12
  },
  /**
   * Values in X,Y,Z
   */
  targetXYZ: {
    x: 0,
    y: 1.2,
    z: 2.5
  },
  /**
   * Min distance and max distance
   */
  limitsMinMax: { min: 8, max: 30 },
  preIntroAnimationX: 3,

  /**
   * field of view
   */
  fieldOfView: 20
};

/**
 * Sizes
 */

export const mobileNavigationBarHeight = 87;

const theme = getTheme(SkapaTheme);
const largestSkapaBreakpoint = theme.breakpoint.xxlarge.width;

const getBreakpoint = () => {
  const currentWidth = window.innerWidth;

  const breakpoint = Object.values(theme.breakpoint).reduce((previousValue, currentValue) => {
    return Math.abs(currentValue.width - currentWidth) <
      Math.abs(previousValue.width - currentWidth)
      ? currentValue
      : previousValue;
  });

  return breakpoint;
};

const isLargestBreakpoint = getBreakpoint().width === largestSkapaBreakpoint;

const selectButtonSize = isLargestBreakpoint ? 'medium' : 'small';

export const shouldUseSmallButtonSize = selectButtonSize === 'small';

const selectIconButtonSize = isLargestBreakpoint
  ? IconButtonSizeEnum.medium
  : IconButtonSizeEnum.small;

export const shouldUseSmallIconButtons = selectIconButtonSize === IconButtonSizeEnum.small;

export const selectGutterSize = getBreakpoint().gridSpacing.gutter;

export const constantOuterMargin = selectGutterSize * 2;

export const selectGutterHalfSize = selectGutterSize * 0.5;

// Based on summaryPage
export const constantMaxWidth = '100rem';

export const constantCustomizeMenuWidth = '30rem';

export interface IMobile {
  $isMobile: boolean;
  $height?: number;
}

export interface IRtl {
  $isRtl: boolean;
}

export const validateUrl = (link: string) => {
  if (!link) {
    return false;
  }

  const https = 'https://';

  const httpsLink = link.startsWith(https) ? link : `${https}${link}`;

  try {
    return new URL(httpsLink);
  } catch {
    return false;
  }
};

export const VIEWER_DEFAULT_ALPHA_INDEX = 10000;

export const NR_OF_SUGGESTIONS_TO_SHOW_BEFORE_SHOW_MORE = 4;

/**
 * Some stores doesn't have a way to write, so sharing will be disabled.
 */
export const disabledStoresForSharing = ['705'];

export enum CriticalCatalogsEnum {
  paxPanels = 'pax-panels',
  skytta = 'skytta'
}

/**
 * Sentry Tags
 */
export enum SentryTags {
  appState = 'appState',
  orientation = 'orientation',
  screenResolution = 'screenResolution',
  screenHeightPixels = 'screenHeightPixels',
  screenWidthPixels = 'screenWidthPixels',
  browserTheme = 'browserTheme',
  insightsConnected = 'insightsConnected',
  isUpptacka = 'isUpptacka',
  isIpexGallery = 'isIpexGallery',
  webpSupport = 'webpSupport',
  coreVersion = 'coreVersion',
  betraktaVersion = 'betraktaVersion',
  kompisVersion = 'kompisVersion',
  insightsVersion = 'insightsVersion',
  abTest = 'abTest',
  abVariation = 'abVariation',
  verboseSentry = 'verboseSentry',
  deviceType = 'deviceType',
  webglContext = 'webglContext',
  insightsIpexId = 'insightsIpexId',
  insightsLongTermId = 'insightsLongTermId',
  insightsSessionId = 'insightsSessionId'
}

export enum WebGLContext {
  Lost = 'Lost',
  Restored = 'Restored',
  CreationError = 'CreationError',
  Normal = 'Normal',
  None = 'None'
}

/**
 * Screenshot directions
 */
export enum ScreenshotDirectionsEnum {
  Front = 'front',
  Left = 'left',
  Right = 'right',
  Behind = 'behind'
}

export const getDataIdByPathname = (pathname: AppRoutesEnum) => {
  return pathname.split('/').slice(-1).pop();
};

export const icfVersion = '1.2';

export enum NavigationOriginEnum {
  Scene = 'scene',
  CustomizeMenu = 'customizeMenu'
}
